import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DataBuildsService } from 'src/app/services/data-builds.service';
import { Pid } from 'src/app/models/pid';
import { IdentityService } from 'src/app/services/identity.service';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { MatChipInputEvent } from '@angular/material/chips';
import { DataBuild } from 'src/app/models/data-build';
import { MenuService } from 'src/app/services/menu.service';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SchemaComponent } from '../schema/schema.component';
import { DialogService } from '../../../services/dialog.service';

@Component({
    selector: 'app-build',
    templateUrl: './build.component.html',
    styleUrls: ['./build.component.scss'],
    standalone: false
})
export class BuildComponent implements OnInit, OnDestroy {
  private dialogs = inject(DialogService);
  @ViewChild(MatSort, { static: false }) sort: MatSort;
  @ViewChild('schemaComponent') schemaComponent: SchemaComponent;

  @Input() isWidget: boolean = false;
  @Input() isNew: boolean = false;
  @Input() teamName: string;
  @Input() rdsName: string;
  @Input() runId: number;
  @Input() username: string;
  @Input() isArchived: boolean;
  @Input() cloneSchema: any = null;

  @Output() onOpenAsset = new EventEmitter<{ assetName: string }>();
  @Output() onOpenTerminal = new EventEmitter<{ command: string; title: string }>();
  @Output() onViewLog = new EventEmitter<{ runId: number; rdsName: string; isArchived: boolean }>();
  @Output() onBuildStarted = new EventEmitter<{ build: DataBuild }>();
  @Output() closeBuild = new EventEmitter();

  private _reloadInterval: any;

  public isLoading: boolean = true;
  public isUpdating: boolean = false;
  public processFilter: 'ALL' | 'RUNNING' | 'COMPLETED' = 'ALL';
  public displayedColumns: string[] = ['status', 'scriptName', 'pid', 'startTime', 'duration'];
  public dataSource = new MatTableDataSource<Pid>([]);
  public allProcesses: Pid[] = [];
  public build: DataBuild = null;
  public tags: Set<string> = new Set();
  public schemaExpanded: boolean = false;

  public get runningCount(): number {
    return this.allProcesses.filter(p => !p.endTime).length;
  }

  public get completedCount(): number {
    return this.allProcesses.filter(p => p.endTime).length;
  }

  public get filteredProcesses(): Pid[] {
    switch (this.processFilter) {
      case 'RUNNING':
        return this.allProcesses.filter(p => !p.endTime);
      case 'COMPLETED':
        return this.allProcesses.filter(p => p.endTime);
      default:
        return this.allProcesses;
    }
  }

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _dataBuildsService: DataBuildsService,
    private _identity: IdentityService,
    private _snackbar: MatSnackBar,
    private _menuService: MenuService
  ) {}

  public ngOnInit(): void {
    (<any>window).build = this;

    if (this.isNew) {
      this.isLoading = false;
      this.schemaExpanded = true;
      return;
    }

    if (this.isWidget) {
      this.load();
    } else {
      this._activatedRoute.params.subscribe((params) => {
        Object.assign(this, {
          teamName: params.team,
          rdsName: params.rds,
          runId: params.runid,
          username: params.username,
          isArchived: params.isArchived == 'true'
        });

        this.load();
        this._menuService.delayedSetActive('builds');
      });
    }
  }

  public ngOnDestroy(): void {
    if (this._reloadInterval) clearInterval(this._reloadInterval);
  }

  public async load(): Promise<void> {
    this.build = await this._dataBuildsService.loadOneAsync(this.teamName, this.runId);
    if (!this.build) {
      this.isLoading = false;
      return;
    }

    this.tags = this.build.tags ? new Set(this.build.tags.split(',')) : new Set();

    const sub = this._dataBuildsService.loadPids(this.teamName, this.rdsName, this.runId, this.isArchived).subscribe({
      error: (error) => {
        console.error(error);
        this.isLoading = false;
      },
      next: (response: Pid[]) => {
        sub.unsubscribe();
        this.isLoading = false;
        this.allProcesses = response;
        this.updateDataSource();

        // Auto-refresh while build is running
        const hasRunningProcesses = response.some(p => !p.endTime);
        if (hasRunningProcesses) {
          if (!this._reloadInterval) {
            this._reloadInterval = setInterval(() => this.load(), 2000);
          }
        } else {
          if (this._reloadInterval) {
            clearInterval(this._reloadInterval);
            this._reloadInterval = null;
          }
        }

        setTimeout(() => {
          this.dataSource.sort = this.sort;
        }, 0);
      }
    });
  }

  public updateDataSource(): void {
    this.dataSource.data = this.filteredProcesses;
  }

  public setFilter(filter: 'ALL' | 'RUNNING' | 'COMPLETED'): void {
    this.processFilter = filter;
    this.updateDataSource();
  }

  public toggleArchived(): void {
    this.isUpdating = true;
    this.isArchived = !this.isArchived;

    if (this.isArchived) this.archive();
    else this.restore();
  }

  public archive(): void {
    const sub = this._dataBuildsService.archive(this.teamName, this.runId).subscribe({
      error: (error) => {
        sub.unsubscribe();
        this.isArchived = !this.isArchived;
        this.isUpdating = false;
        console.error(error);
        this.openSnackBar('An error occurred. See logs for details.', 'OK');
      },
      next: () => {
        sub.unsubscribe();
        this.isUpdating = false;
        if (!this.isWidget) {
          this._router.navigate([this.teamName, 'data', 'build', this.rdsName, this.runId, this.username, true]);
        }
      }
    });
  }

  public restore(): void {
    const sub = this._dataBuildsService.restore(this.teamName, this.runId).subscribe({
      error: (error) => {
        sub.unsubscribe();
        this.isArchived = !this.isArchived;
        this.isUpdating = false;
        console.error(error);
        this.openSnackBar('An error occurred. See logs for details.', 'OK');
      },
      next: () => {
        sub.unsubscribe();
        this.isUpdating = false;
        if (!this.isWidget) {
          this._router.navigate([this.teamName, 'data', 'build', this.rdsName, this.runId, this.username, false]);
        }
      }
    });
  }

  public openAsset(): void {
    if (this.isWidget) {
      this.onOpenAsset.emit({ assetName: this.rdsName });
    } else {
      this._router.navigate([this.teamName, 'data', 'asset', this.rdsName]);
    }
  }

  public viewLog(): void {
    if (this.isWidget && this.build) {
      this.onViewLog.emit({ runId: this.runId, rdsName: this.build.rdsName, isArchived: this.isArchived });
    }
  }

  public launchBuild(): void {
    if (this.schemaComponent) {
      this.schemaComponent.launch();
    }
  }

  public onSchemaLaunched(): void {
    // The schema component has started the build, emit the event
    // The build detail will be handled by the parent widget
  }

  public close(): void {
    this.closeBuild.emit();
  }

  public openSnackBar(message: string, action: string): void {
    const horizontalPosition: MatSnackBarHorizontalPosition = 'center';
    const verticalPosition: MatSnackBarVerticalPosition = 'top';

    this._snackbar.open(message, action, {
      horizontalPosition,
      verticalPosition,
      duration: 10000
    });
  }

  public addTag(event: MatChipInputEvent): void {
    if (event.value) {
      this.tags.add(event.value);
      event.chipInput!.clear();
      this.updateTags();
    }
  }

  public removeTag(keyword: string): void {
    this.tags.delete(keyword);
    this.updateTags();
  }

  public async updateTags(): Promise<void> {
    const tags = [...this.tags].join(',');
    await this._dataBuildsService.updateTagsAsync(this.teamName, this.runId, this.isArchived, tags);
  }

  public async stopBuild(): Promise<void> {
    if (!await this.dialogs.confirm('Are you sure you want to cancel this build?')) return;

    const sub = this._dataBuildsService.stop(this.teamName, this.rdsName, this.runId.toString()).subscribe(() => {
      sub.unsubscribe();
      this.build.isRunning = false;
      this.build.cancelled = true;
    });
  }

  public onSchemaLaunch(event: { build: DataBuild }): void {
    this.onBuildStarted.emit(event);
  }

  public clickProcess(proc: Pid): void {
    // If process has a screen session and user can attach, open terminal
    if (proc.sessionName && this.canAttachToProcess(proc)) {
      const command = `screen -T xterm-256color -d -r ${proc.sessionName}; exit;`;
      const title = `Process: ${proc.scriptName.split(' ')[0]}`;

      if (this.isWidget) {
        this.onOpenTerminal.emit({ command, title });
      }
    }
  }

  public canAttachToProcess(proc: Pid): boolean {
    if (!proc.sessionName || proc.endTime) return false;

    const { username: processUsername, host: processHost } = proc;
    const { username: identityUsername, hosts: identityHosts } = this._identity.me;

    if (processUsername === identityUsername) return true;

    const hostUsernameMapping = identityHosts?.find(({ hostname }) => hostname === processHost);
    return hostUsernameMapping?.username === processUsername;
  }

  public getProcessDuration(proc: Pid): string {
    if (!proc.startTime) return '-';

    const start = new Date(proc.startTime).getTime();
    const end = proc.endTime ? new Date(proc.endTime).getTime() : Date.now();
    const seconds = Math.floor((end - start) / 1000);

    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;

    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  }

  public getStatusIcon(proc: Pid): string {
    if (proc.isRunning) return 'sync';
    if (!proc.endTime) return 'cancel'; // Process stopped without endTime (e.g., killed/cancelled)
    return 'check_circle';
  }

  public getStatusClass(proc: Pid): string {
    if (proc.isRunning) return 'status-running';
    if (!proc.endTime) return 'status-cancelled'; // Process stopped without endTime
    return 'status-complete';
  }
}
